/**
 * Runs async tasks strictly one at a time and remembers the last few
 * successful results by key. Concurrent or repeated requests for the same key
 * share one run; failures are not cached, so a retry runs again.
 *
 * Used for the cover preview still: `@remotion/web-renderer` itself only runs
 * one render at a time, so queueing here means each render's deadline starts
 * when that render actually starts — not while it waits behind another one.
 */
export function createSerialTaskCache<T>(capacity = 4) {
  const results = new Map<string, Promise<T>>();
  let tail: Promise<unknown> = Promise.resolve();

  const remember = (key: string, promise: Promise<T>) => {
    results.delete(key);
    results.set(key, promise);
    while (results.size > capacity) {
      const oldest = results.keys().next().value as string;
      results.delete(oldest);
    }
  };

  return {
    run(key: string, task: () => Promise<T>): Promise<T> {
      const known = results.get(key);
      if (known) {
        remember(key, known);
        return known;
      }
      const promise = tail.catch(() => undefined).then(task);
      tail = promise;
      remember(key, promise);
      promise.catch(() => {
        if (results.get(key) === promise) results.delete(key);
      });
      return promise;
    },
  };
}
